import { Button, Result, Spin, Tag } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";
import React, { useEffect, useState } from "react";
import { Report } from "notiflix";
import moment from "moment";

import HeaderFarms from "../../utils/header_farms";
import Footer from "../../atoms/footer";
import API from "../../services";
import "./approved.css";

const fmt = (d) => (d ? moment(d).format("YYYY-MMMM-DD HH:mm") : "-");
const safe = (v) => (v === null || v === undefined || v === "" ? "-" : v);

function Approved() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const getApproved = () => {
    setLoading(true);
    setError(false);
    API.workflowApprovedDetails(HeaderFarms(), id)
      .then((res) => {
        setData(res?.data ?? null);
      })
      .catch((err) => {
        console.error(err);
        setError(true);
        Report.warning(
          "Oops.. something wrong",
          "Sorry, this application is experiencing problems",
          "Okay"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getApproved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="container approved-detail" style={{ minHeight: 500 }}>
        <div className="text-center" style={{ padding: 80, color: "#B7B7B7" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <>
        <div className="container approved-detail" style={{ minHeight: 500 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/")}
            style={{ marginTop: 16 }}
          >
            Back
          </Button>
          <Result
            status="error"
            title="Approval not found"
            subTitle="The approval document you're looking for is unavailable or has been removed."
          />
        </div>
        <Footer />
      </>
    );
  }

  const ownerName = data?.uid?.fullname || data?.nik?.fullname || data?.created_by;
  const formName = data?.form_id?.name;
  const formId = data?.form_submit_id;
  const detailList = Array.isArray(data?.detail) ? data.detail : [];
  const createdAt = detailList[0]?.created_at;

  const documentItems = [
    { label: "Name", value: safe(formName) },
    { label: "Owned by", value: safe(ownerName) },
    { label: "Form ID", value: safe(formId) },
    { label: "Created at", value: fmt(createdAt) },
  ];

  const isAllApproved = detailList.length > 0 && detailList.every((d) => d?.status);
  const isAnyRejected = detailList.some((d) => d?.rejected);

  const renderTable = (title, items, columns = 2) => {
    if (items.length === 0) return null;
    const rows = [];
    for (let i = 0; i < items.length; i += columns) {
      rows.push(items.slice(i, i + columns));
    }
    return (
      <div className="approved-section">
        <div className="approved-section-title">{title}</div>
        <table className="approved-table">
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((item, ci) => (
                  <React.Fragment key={ci}>
                    <th className="approved-label">{item.label}</th>
                    <td className="approved-value">{item.value}</td>
                  </React.Fragment>
                ))}
                {row.length < columns &&
                  Array.from({ length: columns - row.length }).map((_, idx) => (
                    <React.Fragment key={`empty-${idx}`}>
                      <th className="approved-label approved-empty" />
                      <td className="approved-value approved-empty" />
                    </React.Fragment>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const approverItems = detailList.map((item, idx) => {
    const approverName =
      item?.approved_by?.fullname || item?.approver?.fullname || `Approver ${idx + 1}`;
    const approvedAt = item?.approved_at || item?.approved_time;
    const isApproved = !!item?.status;
    const isRejected = !!item?.rejected;

    const tagColor = isRejected ? "red" : isApproved ? "blue" : "orange";
    const tagText = isRejected ? "Rejected" : isApproved ? "Approved" : "Waiting Approval";
    const tagIcon = isRejected ? (
      <CloseCircleFilled />
    ) : isApproved ? (
      <CheckCircleFilled />
    ) : null;

    return {
      label: approverName,
      value: (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Tag color={tagColor} icon={tagIcon} style={{ margin: 0 }}>
            {tagText}
          </Tag>
          {(isApproved || isRejected) && approvedAt && (
            <span style={{ fontSize: 11, color: "#999" }}>{fmt(approvedAt)}</span>
          )}
        </div>
      ),
    };
  });

  return (
    <>
      <div className="container approved-detail" style={{ minHeight: 500 }}>
        <div className="d-flex align-items-center" style={{ gap: 8, padding: "16px 0" }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/")}>
            Back
          </Button>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Approved Detail</span>
        </div>

        <Result
          status={isAnyRejected ? "error" : isAllApproved ? "success" : "info"}
          title={
            isAnyRejected
              ? "This document has been rejected"
              : isAllApproved
                ? "This document is officially approved"
                : "This document is in review"
          }
        />

        {renderTable("Document Information", documentItems, 2)}

        {approverItems.length > 0 ? (
          renderTable("Approval Information", approverItems, 1)
        ) : (
          <div className="approved-section">
            <div className="approved-section-title">Approval Information</div>
            <div style={{ padding: 16, textAlign: "center", color: "#999" }}>
              No approver data available.
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default Approved;
